import {ref, watch} from 'vue';
import debounce from 'lodash-es/debounce';
import {defineStore} from 'pinia';
import {useRoute, useRouter} from '#imports';

export type RestoreFilterActions = {
    onFilterPresent: (filter: string) => void;
    onFilterAbsent: () => void;
    checkValidity?: (filter: string) => boolean;
};

export const useFilterStore = defineStore('filter-store', () => {
    const route = useRoute();
    const router = useRouter();
    const persistentFilters = ref<Record<string, string>>({});
    const registeredFilters = ref<Array<string>>([]);

    function restoreFilterFromUrlOrStorageIfPresent(
        storageKey: string,
        {onFilterPresent, onFilterAbsent, checkValidity = (_: string) => true}: RestoreFilterActions,
    ) {
        const urlStoredItem = new URLSearchParams(location.search).get(storageKey);
        if (urlStoredItem && checkValidity(urlStoredItem)) {
            onFilterPresent(urlStoredItem);
            return;
        }

        const storedItem = localStorage.getItem(`filters-${storageKey}`);

        if (storedItem && checkValidity(storedItem) && isRegisteredFilter(storageKey)) {
            onFilterPresent(storedItem);
        } else {
            onFilterAbsent();
        }
    }

    function updateFilterRoute(storageKey: string, parameterValue: string): void {
        localStorage.setItem(`filters-${storageKey}`, parameterValue);
        persistentFilters.value[`${storageKey}`] = parameterValue;
    }

    function applyFilterInRoute() {
        const query = {...route.query};

        Object.keys(persistentFilters.value)
            .filter((filterName) => isRegisteredFilter(filterName))
            .forEach((filterName: string) => {
                query[filterName] = persistentFilters.value[filterName]!;
            });

        router.push({query});
    }

    function registerFilter(...filterNames: Array<string>): void {
        filterNames.forEach((filterName) => {
            registeredFilters.value.push(filterName);
        });

        checkStorage();
    }

    function checkStorage() {
        for (const localStorageKey in localStorage) {
            const key = localStorageKey.replace('filters-', '');

            if (localStorageKey.startsWith('filters-') && isRegisteredFilter(key) && localStorage.getItem(localStorageKey)) {
                persistentFilters.value[key] = localStorage.getItem(localStorageKey)!;
            }
        }
    }

    function isRegisteredFilter(filterName: string): boolean {
        return registeredFilters.value.includes(filterName);
    }

    watch(
        () => persistentFilters.value,
        () => {
            debounce(applyFilterInRoute, 200)();
        },
        {deep: true, flush: 'post'},
    );

    return {
        registerFilter,
        restoreFilterFromUrlOrStorageIfPresent,
        updateFilterRoute,
    };
});
